import axios from 'axios';
import { applyTz, createUri, Api } from './utils';

import type { InvoicedEntity } from '@/types/company';

const DATA_URL = createUri(Api.Orgs, '/org/V1');

export type IdMap = {
    [id: string]: string;
};

export type NameMap = {
    companies: IdMap;
    resellers: IdMap;
    locations: IdMap;
};

export type OrgMap = {
    companyLocationMap: Record<string, number[]>;
    companyResellerMap: Record<string, number[]>;
    resellerLocationMap: Record<string, number[]>;
};

export type LocationMap = Record<string, { resellerId: number | null; companyId: number }>;

export enum OrgUrls {
    Countries = 'countries',
    NameMap = 'nameMap',
    OrgMap = 'orgMap',
    LocationMap = 'locationMap',
    Company = 'company',
    Location = 'location',
    Reseller = 'reseller',
    BCCustomers = 'businessCentral/customers',
    LocationEmployees = 'location/employees',
    LocationEmployeesCSV = 'location/employees/csv',
    EmployeeRanks = 'location/employeeRanks',
    EmployeeCutMaterials = 'employee/materials',
    SearchLocations = 'location/search',
    SearchResellers = 'reseller/search',
    SearchCompanies = 'company/search',
    SearchEmployees = 'management/employees/search',
    ProductCatalog = 'productCatalog',
    ProductCatalogs = 'productCatalogs',
    ProductCatalogOrder = 'productCatalogs/order',
    BusinessCentralProducts = 'businessCentral/products',
    NotificationTopics = 'notifications/topics',
    NotificationContacts = 'notifications/contacts',
    TrackingConfig = 'tracking',
    EffectiveConfig = 'effective-config',
}

export type Countries = {
    countryName: string;
}[];

export type LocationResponse = {
    LocationID?: number;
    CompanyID: number;
    /** Direct reseller when the location hangs under one (backend normalizes company vs reseller). */
    ResellerID?: number | null;
    LocationName: string;
    PIN: string;
    Inactive: boolean;
    Country: string;
    ConsentToSell: string | null;
    CutPlanID: number;
    WarrantyPlanID: number;
    TrackingFlag: number;
    WalletTemplate: string | null;
};

export type EffectiveSourceLevel = 'Station' | 'Location' | 'Reseller' | 'Company' | 'Default';

export type EffectiveSetting = {
    key: string;
    label: string;
    value: string | number | boolean | null;
    source: { level: EffectiveSourceLevel; id: number | null; name: string | null };
    /** Most specific level an override can be set at — where "Default" rows send the user. */
    editableAt: { level: EffectiveSourceLevel; id: number | null; name: string | null } | null;
};

export type EffectiveConfigResponse = {
    chain: {
        stationId: number | null;
        serialNum: string | null;
        locationId: number | null;
        locationName: string | null;
        resellerId: number | null;
        resellerName: string | null;
        companyId: number | null;
        companyName: string | null;
    };
    settings: EffectiveSetting[];
};

export enum TrackingFeature {
    Blade = 'Blade',
    Kit = 'Kit',
}

export enum TrackingScopeType {
    Reseller = 'Reseller',
    Company = 'Company',
}

/** One explicit tracking statement at a scope; absence of a row means inherit (reseller -> company -> off). */
export type TrackingConfigRow = {
    Id: number;
    Feature: TrackingFeature;
    ScopeType: TrackingScopeType;
    ScopeID: number;
    Enabled: boolean;
    StartDate: string | null;
    CadenceCuts: number | null;
};

/** Enabled null deletes the scope's row (back to inherit). StartDate is the date cut counting begins from. */
export type PutTrackingConfigInput = {
    Feature: TrackingFeature;
    ScopeType: TrackingScopeType;
    ScopeID: number;
    Enabled: boolean | null;
    CadenceCuts?: number | null;
    StartDate?: string | null;
};

export type CompanyResponse = {
    CompanyID?: number;
    CompanyName: string;
    Inactive: boolean;
    CustomerID?: string | null;
    SalesRepTrackingEnabled: boolean | null;
    DeviceIdEnabled?: boolean | null;
    WatchDuplicateEnabled?: boolean | null;
} & (
    | {
          Reseller: true;
          ResellerCompanyID: number;
      }
    | { Reseller: false; ResellerCompanyID?: number }
) &
    InvoicedEntity;

export type EmployeeResponse = {
    Email: string;
    Name: string;
    LocationID: number;
    ResellerID: number;
    CompanyID: number;
    Status: string;
    TotalCuts: number;
    WarrantyClaimCuts: number;
    SuccessfulCuts: number;
    EmployeeRank: number;
    WarrantyClaimPercentage: number;
};

export const orgApi = axios.create({
    baseURL: DATA_URL,
});

export const fetchOrgData = async <T>(
    token: string,
    url: OrgUrls,
    params?: Record<string, string | number | undefined>,
    tz?: string,
) => {
    const resp = await orgApi.get<T>(url, {
        params: applyTz(params, tz),
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return resp.data;
};

export const fetchOrgGeneric =
    <T>(url: OrgUrls) =>
    async (token: string) => {
        const { data } = await orgApi.get<T>(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return data;
    };
